import React from 'react';
import { useTranslation } from 'react-i18next';
import { IAlbum } from '../../../../data/models/album';
import { useState } from 'react';
import Select from '../../../common/Select/Select';
import { projectServiceApiClient } from '../../../../clients/ProjectServiceApiClient';
import { ProjectDto } from '../../../../data/models/projectsService';
import { IOptions } from '../../../../data/models/option';
import { logger } from '../../../../utils/Logger';
import { projectServicePrsPrefix } from '../../../../utils/helper';
import CreateProject from './CreateProject';
import { useErrorContext } from '../../../../context/ErrorContext';
import styles from './AddToProjectModal.module.scss';
import { isAxiosError } from 'axios';
import { Modal } from '../../../common/Modal';

interface AddToProjectModalProps {
  album: IAlbum;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  onProjectAdded: () => void;
}

export default function AddToProjectModal({
  showModal,
  setShowModal,
  onProjectAdded,
  album,
}: AddToProjectModalProps) {
  const { t } = useTranslation();
  const [project, setProject] = useState<ProjectDto>();
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [createProjectArtistName, setCreateProjectArtistName] = useState(
    album.displayArtist ?? undefined
  );
  const [createProjectName, setCreateProjectName] = useState<string>();
  const { setError } = useErrorContext();

  const loadOptions = (inputText: any, callback: any) => {
    let options: IOptions<ProjectDto>[] = [];
    projectServiceApiClient
      .getProjects(inputText, `${album.label.adminOrgId}`, 0, 100)
      .then((result) => {
        if (result?.data) {
          options = result.data.map((item: ProjectDto) => {
            return { value: item, label: item.name };
          });
        }
        callback(options);
      });
  };

  const handleProjectSelect = (selectedOption: IOptions<ProjectDto>) => {
    setProject(selectedOption?.value ?? undefined);
  };

  const handleCreateProject = async () => {
    if (
      !createProjectName ||
      !createProjectArtistName ||
      createProjectName.trim() === '' ||
      createProjectArtistName.trim() === ''
    ) {
      return;
    }

    try {
      await projectServiceApiClient.createProject(
        createProjectName,
        createProjectArtistName,
        album.label.adminOrgId,
        `${projectServicePrsPrefix}${album.id}`
      );
      onProjectAdded();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        setError(t('product.addToProjectModal.projectConflictError'));
      } else {
        setError(t('common.error.defaultError'));
      }
    }
  };

  const handleAddProjectContent = async () => {
    if (project) {
      await projectServiceApiClient.postProjectContent(
        project.id,
        `${projectServicePrsPrefix}${album.id}`
      );

      onProjectAdded();
    }
  };

  const handleSubmit = async () => {
    try {
      if (showCreateProject) {
        await handleCreateProject();
      } else {
        await handleAddProjectContent();
      }
    } catch (error: any) {
      logger.error(error);
    }

    closePopUp();
  };

  const closePopUp = () => {
    setShowModal(false);
  };

  const submitDisabled = () => {
    if (showCreateProject) {
      return (
        !createProjectArtistName ||
        createProjectArtistName.trim() === '' ||
        !createProjectName ||
        createProjectName.trim() === ''
      );
    }

    return !project;
  };

  return (
    <Modal
      titleText={t('product.addToProjectModal.AddProductToProject')}
      submitButtonText={
        showCreateProject
          ? t('product.addToProjectModal.createProject')
          : t('product.addToProjectModal.AddProduct')
      }
      show={showModal}
      submitButtonDisabled={submitDisabled()}
      onClose={closePopUp}
      onSubmit={handleSubmit}
    >
      {showCreateProject ? (
        <CreateProject
          artistName={createProjectArtistName}
          projectName={createProjectName}
          onArtistNameChange={setCreateProjectArtistName}
          onProjectNameChange={setCreateProjectName}
        />
      ) : (
        <>
          <div className={styles['create-project']}>
            <span>{t('product.addToProjectModal.projectNotFound')}</span>
            <button
              type="button"
              className={`btn btn-primary alt ${styles['create-project-button']}`}
              onClick={() => setShowCreateProject(true)}
            >
              {t('product.addToProjectModal.createProjectText')}
            </button>
          </div>

          <Select
            id="projectTypeahead"
            isClearable
            loadOptions={loadOptions}
            placeholder=""
            onChange={handleProjectSelect}
          ></Select>
        </>
      )}
    </Modal>
  );
}
